import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHead } from "@/components/site/Motion";
import { useEnquiry } from "@/context/EnquiryContext";
import { COUNTRIES, REGION_PHOTOS, ZONES } from "@/data/site";
import { usePhotos } from "@/hooks/usePhotos";
import { T } from "@/context/ContentContext";

const spring = { type: "spring", stiffness: 380, damping: 34 };
const ease = [0.22, 1, 0.36, 1];

function RegionRail({ active, setActive }) {
  return (
    <div className="flex lg:flex-wrap gap-1.5 overflow-x-auto lg:overflow-visible no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 pb-1" role="tablist" data-testid="countries-region-rail">
      {ZONES.map((z) => {
        const on = active === z.slug;
        const count = COUNTRIES.filter(([, zone]) => zone === z.slug).length;
        return (
          <button key={z.slug} role="tab" aria-selected={on} onClick={() => setActive(z.slug)} data-testid={`countries-region-${z.slug}`}
            className="relative shrink-0 flex items-center gap-2.5 rounded-full border border-ink/10 px-4 py-2.5 text-left">
            {on && <motion.span layoutId="region-pill" transition={spring} className="absolute inset-0 rounded-full bg-ink" />}
            <span className={`relative z-10 font-display font-bold whitespace-nowrap transition-colors duration-300 ${on ? "text-cream" : "text-ink/70 hover:text-ink"}`}>{z.name}</span>
            <span className={`relative z-10 font-mono text-xs transition-colors duration-300 ${on ? "text-gold" : "text-stone-600"}`}>{String(count).padStart(2, "0")}</span>
          </button>
        );
      })}
    </div>
  );
}

function CountryTile({ name, zone, i, photo }) {
  const georgia = name === "Georgia";
  return (
    <motion.div initial={{ opacity: 0, y: 18, filter: "blur(6px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.55, delay: i * 0.035, ease }} className={georgia ? "col-span-2" : ""}>
      <Link to={georgia ? "/georgia" : `/trips?zone=${zone}`} data-testid={`country-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className={`group flex h-full items-center justify-between gap-2 sm:gap-3 rounded-2xl border px-3 sm:px-4 py-3 sm:py-3.5 transition-[background-color,border-color,color,transform] duration-300 hover:-translate-y-0.5 ${
          georgia ? "bg-wine text-cream border-wine hover:bg-terra hover:border-terra" : "bg-paper border-ink/10 text-ink hover:bg-ink hover:text-cream hover:border-ink"}`}>
        <span className="flex items-center gap-2.5 sm:gap-3 min-w-0">
          {photo ? (
            <img src={photo.replace("w=1400", "w=160").replace("w=1800", "w=160")} alt="" loading="lazy"
              className={`h-8 w-8 sm:h-9 sm:w-9 rounded-full object-cover shrink-0 ring-2 ${georgia ? "ring-gold" : "ring-paper"} transition-transform duration-500 group-hover:scale-110`} />
          ) : (
            <span className={`grid place-items-center h-8 w-8 sm:h-9 sm:w-9 rounded-full shrink-0 font-mono text-[11px] ${georgia ? "bg-gold text-ink" : "bg-cream text-ink/70 group-hover:bg-cream/15 group-hover:text-cream"} transition-colors`}>
              {name.slice(0, 2).toUpperCase()}
            </span>
          )}
          <span className={`font-display font-bold leading-tight ${georgia ? "text-xl" : "text-[15px] sm:text-lg"}`}>{name}</span>
          {georgia && <span className="eyebrow text-[10px] rounded-full bg-gold text-ink px-2 py-0.5 shrink-0">No. 1</span>}
        </span>
        <ArrowUpRight className="hidden sm:block h-4 w-4 shrink-0 opacity-50 transition-[transform,opacity] duration-300 group-hover:opacity-100 group-hover:rotate-45" />
      </Link>
    </motion.div>
  );
}

export function CountriesHighlight() {
  const [active, setActive] = useState(ZONES[0].slug);
  const { openEnquiry } = useEnquiry();
  const photos = usePhotos();
  const zone = ZONES.find((z) => z.slug === active);
  const list = COUNTRIES.filter(([, z]) => z === active);

  return (
    <section className="bg-paper py-24 sm:py-32" data-testid="countries-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead k="home.countries" eyebrow={`${ZONES.length} regions · ${COUNTRIES.length} countries`} title={["Georgia first.", "Then the world."]}>
          <T k="home.countries.intro" as="p" className="max-w-sm text-stone-600">Pick a region to see where we can take you. From the Caucasus to the Caribbean, group and custom trips.</T>
        </SectionHead>
        <Reveal className="rounded-[32px] bg-cream border border-ink/5 p-4 sm:p-6 lg:p-8 min-w-0">
          <RegionRail active={active} setActive={setActive} />
          <div className="mt-8 pt-8 border-t border-ink/10 min-w-0" data-testid="countries-panel">
            <motion.div key={active} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, ease }}
              className="relative overflow-hidden rounded-[24px] sm:rounded-t-[200px] sm:rounded-b-[16px] aspect-[4/3] sm:aspect-[21/8] mb-6" data-testid="countries-region-photo">
              <motion.img src={photos.region(active)} alt={REGION_PHOTOS[active]?.[1]} initial={{ scale: 1.15 }} animate={{ scale: 1 }}
                transition={{ duration: 1.4, ease }} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/45 to-ink/5" />
              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 flex flex-col sm:flex-row sm:items-end justify-between gap-4 text-cream">
                <div>
                  <p className="eyebrow text-gold">{list.length} countries · <T k={`region.${active}.caption`}>{REGION_PHOTOS[active]?.[1]}</T></p>
                  <h3 className="mt-2 font-display text-3xl sm:text-5xl font-extrabold tracking-tight" data-testid="countries-panel-title">{zone?.name}</h3>
                </div>
                <button onClick={() => openEnquiry({ trip_title: `Custom trip: ${zone?.name}` })} data-testid="countries-plan-btn"
                  className="self-start sm:self-auto whitespace-nowrap rounded-full bg-paper text-ink px-5 py-2.5 text-sm font-semibold hover:bg-terra hover:text-paper transition-colors">
                  <T k="home.countries.plan">Plan your</T> {zone?.name} <T k="home.countries.trip">trip</T>
                </button>
              </div>
            </motion.div>
            <div key={`grid-${active}`} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3" data-testid="countries-list">
              {list.map(([name], i) => <CountryTile key={name} name={name} zone={active} i={i} photo={photos.country(name)} />)}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
